import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, Loading } from '@/components/ui/daisy';
import useExamActivities from '@/data/hook/exam/useExamActivities';
import useExamAreas from '@/data/hook/exam/useExamAreas';
import useExamSettingsTo from '@/data/hook/exam/useExamSettingsTo';
import Exam from '@/models/Exam';

import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '../icons';
import ExamAreasFilter, {
  ALL_AREAS,
  ExamAreaFilterValue,
} from './ExamAreasFilter';
import ExamBlockFormList from './ExamBlockFormList';
import { ExamBlockFormFields } from './ExamBlockInlineForm';
import ExamAreaQuestionsList from './ExamAreaQuestionsList';

type ExamQuestionsTabProps = {
  exam: Exam;
};

export default function ExamQuestionsTab({ exam }: ExamQuestionsTabProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace.questions',
  });

  const examSettingsTo = useExamSettingsTo();

  const {
    examAreas,
    isLoading: isLoadingAreas,
    cacheManager: examAreaCacheManager,
  } = useExamAreas({
    examId: exam.id,
  });

  const [selectedAreaId, setSelectedAreaId] =
    useState<ExamAreaFilterValue>(ALL_AREAS);

  const {
    activitiesByAreaId,
    isLoading: isLoadingActivities,
    cacheManager,
    createActivity,
    isCreating,
    updateActivity,
    isUpdating,
    reorderActivity,
    isReordering,
    deleteActivity,
    replaceVersionedActivity,
  } = useExamActivities({ examAreas, examAreaCacheManager });

  const visibleExamAreas = useMemo(() => {
    return examAreas.filter(ea => selectedAreaId === ALL_AREAS || ea.id === selectedAreaId);
  }, [examAreas, selectedAreaId]);

  const handleCreate = async (data: ExamBlockFormFields) => {
    const result = await createActivity({
      examAreaId: data.examAreaId,
      data: {
        name: data.name,
        timeLimit: data.hasTime ? data.timeLimit : null,
        shuffleQuestions: data.shuffleQuestions,
      },
    });
    if (selectedAreaId !== ALL_AREAS && selectedAreaId !== data.examAreaId) {
      setSelectedAreaId(data.examAreaId);
    }
    return result;
  };

  if (!isLoadingAreas && examAreas.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <Alert variant="soft" className="rounded-2xl">
          <InformationCircleIcon className="w-6 h-6 shrink-0 text-info" />
          <span className="text-sm">{t('noAreasFallback')}</span>
        </Alert>
        {examSettingsTo && (
          <div className="flex justify-end">
            <Link to={examSettingsTo.to}>
              <Button size="sm">
                {t('goToSettings')}
                <ArrowTopRightOnSquareIcon className="w-3 h-3" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  }

  if (isLoadingAreas || isLoadingActivities) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ExamAreasFilter
        examAreas={examAreas}
        value={selectedAreaId}
        onChange={setSelectedAreaId}
      />
      <div className="relative">
        <div className="flex flex-col gap-0">
          {visibleExamAreas.map((examArea) => (
            <ExamAreaQuestionsList
              key={examArea.id}
              activities={activitiesByAreaId.get(examArea.id) ?? []}
              examArea={examArea}
              exam={exam}
              examAreas={examAreas}
              cacheManager={cacheManager}
              isUpdating={isUpdating}
              onUpdate={updateActivity}
              onDelete={deleteActivity}
              onVersioned={replaceVersionedActivity}
              isReordering={isReordering}
              onReorder={reorderActivity}
            />
          ))}
        </div>
        <ExamBlockFormList
          exam={exam}
          examAreas={examAreas}
          onSubmit={handleCreate}
          isLoading={isCreating}
        />
      </div>
    </div>
  );
}
